// Connectors (fn-72.4 / R6) — a PREVIEW-only, ADDITIVE dual-panel view. Left = a
// connector list (the DECLARED config-repo MCP servers, `mcpServers.list`);
// right = the selected connector's tools, split into groups by prefix. The
// gateway-backed connector (its URL is the platform AgentCore gateway) is fed
// the LIVE registered tool catalog (`gatewayTools.list`); the grouping/merge
// logic lives in `merge.ts`. Mirrors Claude AI's Connectors layout. The
// standalone MCP Servers + Gateway Tools pages are untouched — this is a lens,
// gated behind the user-menu "preview features" toggle. Selection lives in the
// URL (`?connector=<name>`) so it is deep-linkable and survives refresh/back.

import { keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { trpc } from '../../trpc-client.js';
import { formatInputSchema, formatReadWrite, readWriteChipClass } from '../gateway-tools/list.js';
import { formatMcpStatus } from '../mcp-servers/list.js';
import {
  type Connector,
  type ConnectorTool,
  buildConnectors,
  findConnector,
  prettifyConnectorName,
} from './merge.js';

// Fetch ALL declared servers in one page (no pagination UI on Connectors); the
// max page size the mcp-servers list supports. A truncation notice fires when
// more exist than this single page (`next_cursor` present).
const MCP_PAGE_LIMIT = 100;

// ───────────────────────── Presentational (pure, prop-driven) ─────────────

export interface ConnectorsViewProps {
  readonly connectors: readonly Connector[];
  readonly selectedName: string | null;
  readonly onSelect: (name: string) => void;
  readonly isPending: boolean;
  /** Non-blocking: declared (mcp-servers) query failed. */
  readonly mcpErrorMessage: string | null;
  /** The gateway-tools (live) query failed/forbidden — gates the detail copy. */
  readonly gatewayErrorMessage: string | null;
  readonly gatewayForbidden: boolean;
  /** From a successful gateway query — `false` is a normal base-posture state. */
  readonly gatewayConfigured: boolean;
  /** The live gateway query is still loading (no endpoint to attribute by yet). */
  readonly gatewayPending: boolean;
  /** More declared servers exist than the single fetched page. */
  readonly truncated: boolean;
}

export function ConnectorsView(props: ConnectorsViewProps): React.ReactElement {
  const header = (
    <div className="skills-header">
      <div>
        <h1>Connectors</h1>
        <p className="skills-header-meta">
          Declared MCP servers and their live gateway tools, merged.
        </p>
      </div>
    </div>
  );

  if (props.isPending && props.connectors.length === 0) {
    return (
      <section className="page-connectors" aria-busy="true">
        {header}
        <p className="skills-empty">Loading connectors…</p>
      </section>
    );
  }

  const selected = findConnector(props.connectors, props.selectedName);

  return (
    <section className="page-connectors">
      {header}

      {props.mcpErrorMessage !== null && (
        <p className="skills-error" role="alert">
          Failed to load declared MCP servers: {props.mcpErrorMessage}. Showing live gateway targets
          only.
        </p>
      )}
      {/* Page-level gateway notice — always visible (not only inside a selected
          connector's detail) so a forbidden/failed live query is surfaced even
          with no selection or an empty list. */}
      {props.gatewayForbidden ? (
        <p className="skills-empty" role="note">
          Live gateway tools require platform-admin access — showing declared connectors only.
        </p>
      ) : props.gatewayErrorMessage !== null ? (
        <p className="skills-error" role="alert">
          Failed to load live gateway tools: {props.gatewayErrorMessage}. Showing declared
          connectors only.
        </p>
      ) : null}
      {props.truncated && (
        <p className="skills-empty">
          Showing the first {MCP_PAGE_LIMIT} declared servers — more exist. Use the MCP Servers page
          to browse them all.
        </p>
      )}

      <div className="connectors-body">
        <nav className="connectors-sidebar" aria-label="Connectors">
          {props.connectors.length === 0 ? (
            <p className="skills-empty">No connectors.</p>
          ) : (
            <ul className="connectors-list">
              {props.connectors.map((connector) => {
                const isSelected = selected !== undefined && connector.name === selected.name;
                return (
                  <li key={connector.name}>
                    <button
                      type="button"
                      className={isSelected ? 'connector-item is-selected' : 'connector-item'}
                      aria-current={isSelected ? 'true' : undefined}
                      onClick={() => props.onSelect(connector.name)}
                    >
                      <span className="connector-item-name">{connector.displayName}</span>
                      <span className="connector-item-meta">
                        <ConnectorSourceChips connector={connector} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        <div className="connectors-detail">
          {selected === undefined ? (
            <p className="skills-empty connectors-detail-empty">
              {props.selectedName === null
                ? 'Select a connector to see its tools.'
                : `Connector "${props.selectedName}" not found.`}
            </p>
          ) : (
            <ConnectorDetail
              connector={selected}
              gatewayErrorMessage={props.gatewayErrorMessage}
              gatewayForbidden={props.gatewayForbidden}
              gatewayConfigured={props.gatewayConfigured}
              gatewayPending={props.gatewayPending}
            />
          )}
        </div>
      </div>
    </section>
  );
}

/** A small chip summarizing a connector: its live tool count, or "external". */
function ConnectorSourceChips({ connector }: { connector: Connector }): React.ReactElement | null {
  if (connector.ownership === 'external') {
    return <span className="sb-chip connector-chip">external</span>;
  }
  if (connector.ownership === 'gateway' && connector.toolCount > 0) {
    return (
      <span className="sb-chip connector-chip connector-chip-live">
        {connector.toolCount} {connector.toolCount === 1 ? 'tool' : 'tools'}
      </span>
    );
  }
  // 'unknown', or gateway with 0 tools — no chip; the detail explains.
  return null;
}

function ConnectorDetail({
  connector,
  gatewayErrorMessage,
  gatewayForbidden,
  gatewayConfigured,
  gatewayPending,
}: {
  readonly connector: Connector;
  readonly gatewayErrorMessage: string | null;
  readonly gatewayForbidden: boolean;
  readonly gatewayConfigured: boolean;
  readonly gatewayPending: boolean;
}): React.ReactElement {
  const { declared } = connector;
  return (
    <div className="connector-detail-body">
      <div className="skill-card-header connector-detail-header">
        <span className="skill-card-name">{connector.displayName}</span>
        <span className="connector-detail-sources">
          <ConnectorSourceChips connector={connector} />
        </span>
      </div>

      {declared !== null && (
        <div className="skill-card-meta">
          <span className="skill-meta-item">
            <span className="skill-meta-label">Type</span>
            <span className="skill-meta-value is-plain">{declared.server_type}</span>
          </span>
          {declared.url !== null && declared.url.length > 0 && (
            <span className="skill-meta-item">
              <span className="skill-meta-label">Endpoint</span>
              <span className="skill-meta-value is-plain">
                <code>{declared.url}</code>
              </span>
            </span>
          )}
          <span className="skill-meta-item">
            <span className="skill-meta-label">Declared status</span>
            <span className={`sb-chip mcp-status-${declared.scan_status}`}>
              {formatMcpStatus(declared.scan_status)}
            </span>
          </span>
        </div>
      )}

      <h2 className="connector-tools-heading">Tools</h2>
      <ConnectorToolGroups
        connector={connector}
        gatewayErrorMessage={gatewayErrorMessage}
        gatewayForbidden={gatewayForbidden}
        gatewayConfigured={gatewayConfigured}
        gatewayPending={gatewayPending}
      />
    </div>
  );
}

/** A single tool row (name + read/write hint + description + input schema). */
function ToolRow({ tool }: { tool: ConnectorTool }): React.ReactElement {
  return (
    <li className="gateway-tool-row">
      <div className="gateway-tool-row-head">
        <span className="gateway-tool-name">{tool.name}</span>
        <span className={readWriteChipClass(tool.readWrite)}>
          {formatReadWrite(tool.readWrite)}
        </span>
      </div>
      {tool.description !== null && tool.description.length > 0 && (
        <p className="gateway-tool-description">{tool.description}</p>
      )}
      {formatInputSchema(tool.inputSchema) !== null && (
        <details className="gateway-tool-schema">
          <summary>Input schema</summary>
          <pre className="gateway-tool-schema-body">{formatInputSchema(tool.inputSchema)}</pre>
        </details>
      )}
    </li>
  );
}

/** A connector's tools, split into prefix groups — or the right non-silent notice. */
function ConnectorToolGroups({
  connector,
  gatewayErrorMessage,
  gatewayForbidden,
  gatewayConfigured,
  gatewayPending,
}: {
  readonly connector: Connector;
  readonly gatewayErrorMessage: string | null;
  readonly gatewayForbidden: boolean;
  readonly gatewayConfigured: boolean;
  readonly gatewayPending: boolean;
}): React.ReactElement {
  const externalNote = (
    <p className="skills-empty" role="note">
      This connector's tools are served from its own MCP endpoint and aren't introspected here.
    </p>
  );

  // Ownership UNKNOWN: the live query gave us no endpoint to compare against, so
  // surface the gateway state — NEVER claim "external" just because the query
  // failed (Codex). The flags disambiguate forbidden / error / pending / absent.
  if (connector.ownership === 'unknown') {
    if (gatewayForbidden) {
      return (
        <p className="skills-empty" role="note">
          Live gateway tools require platform-admin access.
        </p>
      );
    }
    if (gatewayErrorMessage !== null) {
      return (
        <p className="skills-error" role="alert">
          Failed to load live gateway tools: {gatewayErrorMessage}
        </p>
      );
    }
    if (gatewayPending) {
      return (
        <p className="skills-empty" aria-busy="true">
          Loading tools…
        </p>
      );
    }
    // A clean read with no endpoint ⇒ no gateway configured this stage.
    return gatewayConfigured ? (
      externalNote
    ) : (
      <p className="skills-empty">No gateway configured for this stage.</p>
    );
  }

  // Known EXTERNAL: its URL differs from the introspected gateway endpoint.
  if (connector.ownership === 'external') return externalNote;

  // ownership === 'gateway' — render its prefix groups. Failed targets ride as
  // warnings (never silently dropped) alongside whatever loaded.
  const targetErrorBanners =
    connector.targetErrors.length > 0
      ? connector.targetErrors.map((err) => (
          <p key={err.name} className="skills-error" role="alert">
            Target "{prettifyConnectorName(err.name)}" failed to load — {err.code}.
          </p>
        ))
      : null;

  if (connector.toolGroups.length === 0) {
    if (targetErrorBanners !== null) return <>{targetErrorBanners}</>;
    return <p className="skills-empty">No tools registered for this connector.</p>;
  }

  return (
    <>
      {targetErrorBanners}
      {connector.toolGroups.map((group) => (
        <section key={group.prefix} className="connector-tool-group">
          <h3 className="connector-tool-group-label">{group.label}</h3>
          <ul className="gateway-target-tools" aria-label={`${group.label} tools`}>
            {group.tools.map((tool) => (
              <ToolRow key={tool.id} tool={tool} />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

// ───────────────────────── Data-fetching wrapper ──────────────────────────

/** tRPC FORBIDDEN / HTTP 403 detection (mirrors `use-is-platform-admin`). */
function isForbiddenError(error: unknown): boolean {
  const data = (error as { data?: { code?: string; httpStatus?: number } } | null)?.data;
  return data?.code === 'FORBIDDEN' || data?.httpStatus === 403;
}

export function ConnectorsListPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedName = searchParams.get('connector');

  const mcpServersQuery = trpc.mcpServers.list.useQuery(
    { limit: MCP_PAGE_LIMIT, sort_by: 'name', sort_dir: 'asc' },
    { placeholderData: keepPreviousData },
  );
  const gatewayToolsQuery = trpc.gatewayTools.list.useQuery(undefined, { staleTime: 0 });

  const servers = mcpServersQuery.data?.data ?? [];
  const truncated = (mcpServersQuery.data?.next_cursor ?? null) !== null;

  // null targets ⇒ the gateway query is unavailable (pending/error/forbidden):
  // build from declared servers only and surface why in the detail panel.
  const targets = gatewayToolsQuery.data?.targets ?? null;
  // The API tells us which endpoint these tools came from; a declared server
  // owns them when its URL matches (no provider-specific detection here).
  const gatewayUrl = gatewayToolsQuery.data?.gateway_url ?? null;
  const connectors = buildConnectors({ servers, targets, gatewayUrl });

  return (
    <ConnectorsView
      connectors={connectors}
      selectedName={selectedName}
      onSelect={(name) => {
        const next = new URLSearchParams(searchParams);
        next.set('connector', name);
        setSearchParams(next, { replace: true });
      }}
      isPending={
        connectors.length === 0 &&
        (mcpServersQuery.isPending || gatewayToolsQuery.isPending) &&
        mcpServersQuery.error === null &&
        gatewayToolsQuery.error === null
      }
      mcpErrorMessage={mcpServersQuery.error !== null ? mcpServersQuery.error.message : null}
      gatewayErrorMessage={
        gatewayToolsQuery.error !== null ? gatewayToolsQuery.error.message : null
      }
      gatewayForbidden={isForbiddenError(gatewayToolsQuery.error)}
      gatewayConfigured={gatewayToolsQuery.data?.gateway_configured ?? false}
      gatewayPending={gatewayToolsQuery.isPending}
      truncated={truncated}
    />
  );
}
